// RegenContextSpec — Sprint 19BH Day 2 Step 2.2 · v4 manifest declarative field.
//
// v4-rewrite.md §2.3 + §14: when a user triggers regen for an item (e.g.
// flags an MCQ as "rewrite this"), the backend (`packages/ingest/src/context/
// gather.ts` · Day 4 Step 4.4) needs to know what context to assemble for the
// LLM call — what previous items to include for dedup, what source material
// to attach, whether other chapters' content of the same kind helps avoid
// repetition, etc.
//
// In v3 every regen path hand-coded its context gathering. v4 hoists this
// into the manifest so:
//   - Adding a new component = describe its context needs declaratively
//     (extensibility checkpoint plan §12.1)
//   - Cost/quality tuning a stage's regen = edit one flag (no code review of
//     workflow internals)
//   - Audit-driven `fill_audit_gap` action reuses the same context spec
//     (gap → suggestedAction = add scope, then add path reads regenContext)
//
// Per v4-rewrite.md §14 context strategy table — examples Step 2.3 will fill:
//   mcq/tf/fc:    fullOutline + sameStageInChapter='always' + chapLectureGlossary + chapSource
//   essay:        fullOutline + sameStageInChapter='always' (all chapters) + fullCourseSource
//   lecture_note: fullOutline + sameStageInChapter='always' + chapSource + fullCourseSource +
//                 otherChaptersSameKind (for cross-chapter consistency)
//   mock_paper:   fullOutline + fullCourseSource (mock papers are independent)
import { z } from 'zod';
/**
 * Whether to include other items of the same stage from the same chapter in
 * the regen prompt context.
 *
 *   - `'always'`     — always include (subjective stages, dedup-critical)
 *   - `'when_dedup'` — include only when the active manifest invariant
 *                      includes `dedup_by_concept` (cost-frugal)
 *   - `'never'`      — never include (independent items, e.g. mock papers)
 */
export const SAME_STAGE_IN_CHAPTER_VALUES = ['always', 'when_dedup', 'never'];
export const SameStageInChapterSchema = z.enum(SAME_STAGE_IN_CHAPTER_VALUES);
/**
 * Declarative description of the context an LLM regen call for this stage
 * needs. Read by `packages/ingest/src/context/gather.ts` (Day 4) — no stage-
 * specific branching in regenerate-workflow.
 *
 * `needsFullOutline` is required `true` for every v4 stage (anchor-first is
 * Pillar B). Future post-v4 sprints may relax — leave as literal `true` for
 * now to make the design intent explicit.
 */
export const RegenContextSpecSchema = z.object({
    /**
     * Always `true` in v4 (Pillar B · frozen outline as ctx). Field kept
     * explicit (not implied) so future post-v4 sprints can introduce a stage
     * that doesn't need the full outline (e.g. a quote-formatting stage
     * scoped to single chapter) without retroactively changing every entry.
     */
    needsFullOutline: z.literal(true),
    needsSameStageInChapter: SameStageInChapterSchema,
    needsChapterLectureAndGlossary: z.boolean(),
    needsChapterSource: z.boolean(),
    needsFullCourseSource: z.boolean(),
    needsOtherChaptersOfSameKind: z.boolean(),
}).strict();
/**
 * Default skeleton for new manifest entries — meaningful "nothing needed
 * beyond outline" baseline. Step 2.2 uses this for placeholder values
 * before Step 2.3 fills the real per-stage spec.
 */
export const DEFAULT_REGEN_CONTEXT_SPEC = {
    needsFullOutline: true,
    needsSameStageInChapter: 'never',
    needsChapterLectureAndGlossary: false,
    needsChapterSource: false,
    needsFullCourseSource: false,
    needsOtherChaptersOfSameKind: false,
};
//# sourceMappingURL=regen-context-spec.js.map